/**
 * Code Display Component.
 *
 * Fetches and renders the barcode or QR code image for an asset based
 * on the organization's preference. Allows toggling between formats
 * and provides a download link for printing labels.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { Download, QrCode, Barcode } from "lucide-react";
import type { BarcodeData } from "@/types/asset";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";

interface BarcodeDisplayProps {
  assetId: string;
  sku: string;
}

function BarcodeDisplay({ assetId, sku }: BarcodeDisplayProps) {
  const [formatOverride, setFormatOverride] = useState<string | null>(null);

  const { data, isLoading } = useQuery<BarcodeData>({
    queryKey: ["assets", assetId, "barcode", formatOverride],
    queryFn: async () => {
      const params = formatOverride ? `?format=${formatOverride}` : "";
      return (await apiClient.get(`/api/v1/assets/${assetId}/barcode${params}`))
        .data as BarcodeData;
    },
    enabled: !!assetId,
  });

  const activeFormat = formatOverride || data?.code_format || "barcode";
  const isQR = activeFormat === "qrcode";
  const label = isQR ? "QR Code" : "Barcode";

  const handleDownload = () => {
    if (!data) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${data.barcode_base64}`;
    link.download = `${sku}-${activeFormat}.png`;
    link.click();
  };

  return (
    <Card padding="sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">
          {label}
        </h3>
        {/* Format toggle */}
        <div className="flex items-center gap-1 rounded-[var(--radius-pill)] border border-[var(--border-default)] p-0.5">
          <button
            onClick={() => setFormatOverride("barcode")}
            className={`flex items-center gap-1 rounded-[var(--radius-pill)] px-2.5 py-1 text-[11px] font-medium transition-colors ${
              !isQR
                ? "bg-[var(--color-interactive-primary)] text-[var(--text-inverse)]"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            }`}
          >
            <Barcode className="h-3 w-3" /> Barcode
          </button>
          <button
            onClick={() => setFormatOverride("qrcode")}
            className={`flex items-center gap-1 rounded-[var(--radius-pill)] px-2.5 py-1 text-[11px] font-medium transition-colors ${
              isQR
                ? "bg-[var(--color-interactive-primary)] text-[var(--text-inverse)]"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            }`}
          >
            <QrCode className="h-3 w-3" /> QR Code
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <p className="text-sm text-[var(--text-tertiary)]">
            Generating {label.toLowerCase()}...
          </p>
        </div>
      ) : data ? (
        <div className="flex flex-col items-center gap-3">
          <div
            className={`bg-white rounded-[var(--radius-md)] p-3 ${isQR ? "w-fit" : "w-full"}`}
          >
            <img
              src={`data:image/png;base64,${data.barcode_base64}`}
              alt={`${label} for ${sku}`}
              className={isQR ? "h-40 w-40 object-contain" : "max-w-full"}
              data-no-dim
            />
          </div>
          <p className="text-xs font-mono text-[var(--text-secondary)]">
            {sku}
          </p>
          <Button size="sm" variant="secondary" onClick={handleDownload}>
            <Download className="h-3.5 w-3.5" /> Download {label}
          </Button>
        </div>
      ) : (
        <p className="text-sm text-[var(--text-tertiary)] py-4 text-center">
          Unable to generate {label.toLowerCase()}.
        </p>
      )}
    </Card>
  );
}

export default BarcodeDisplay;
